/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { message, Spin } from 'antd';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import patientApi from '../../api/patient.api';
import { PageShell, Card, Section, Kpi, StatusPill, EmptyState } from '../../ui';

const PAGE_SIZE = 20;
const GENDER_LABEL = { MALE: 'Мъж', FEMALE: 'Жена' };

/* ---------------------------------------------------------------- */
/* Helpers (mirror Patient Detail / Patient List design)            */
/* ---------------------------------------------------------------- */
const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return String(iso);
  return `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.${y}`;
};

const fmtEgn = (egn) => {
  if (!egn) return null;
  return egn.length >= 6 ? `${egn.slice(0, 6)} ${egn.slice(6)}` : egn;
};

const ageFrom = (iso) => {
  const today = new Date();
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  let a = today.getFullYear() - y;
  if (today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d)) a--;
  return a;
};

const dobDisplay = (p) => {
  if (p.dateOfBirth) return fmtDate(p.dateOfBirth);
  if (p.yearOfBirth) return String(p.yearOfBirth);
  return '—';
};

const ageDisplay = (p) => {
  if (p.dateOfBirth) return ageFrom(p.dateOfBirth);
  if (p.yearOfBirth) return new Date().getFullYear() - p.yearOfBirth;
  return null;
};

const patientHref = (p) => `/patients/medical-profile/${p.id}`;

const initials = (name) => {
  const parts = (name || '').trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '')).toUpperCase() || 'П';
};

/* ---------------------------------------------------------------- */
/* CountUp — animated counter (dashboard parity)                    */
/* ---------------------------------------------------------------- */
const useCountUp = (target, { duration = 900 } = {}) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (typeof target !== 'number' || target === 0) { setVal(target || 0); return; }
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVal(target); return;
    }
    let raf, start;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      if (start == null) start = now;
      const p = Math.min((now - start) / duration, 1);
      setVal(Math.round(ease(p) * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
};

const CountUp = ({ value, suffix = '' }) => {
  const n = useCountUp(value);
  return <>{n}{suffix}</>;
};

/* ---------------------------------------------------------------- */
/* Hero header                                                      */
/* ---------------------------------------------------------------- */
const PageHero = () => (
  <div className="pt-hero">
    <svg className="pt-hero-decor" viewBox="0 0 320 200" fill="none" aria-hidden="true" preserveAspectRatio="xMaxYMid slice">
      <circle cx="250" cy="100" r="120" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="250" cy="100" r="86" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="250" cy="100" r="52" stroke="currentColor" strokeWidth="1.5" />
      <g fill="currentColor">
        {Array.from({ length: 24 }).map((_, i) => {
          const c = i % 6, r = Math.floor(i / 6);
          return <circle key={i} cx={188 + c * 26} cy={36 + r * 26} r="2" />;
        })}
      </g>
    </svg>
    <div className="pt-hero-inner">
      <span className="pt-hero-icon"><FeatherIcon icon="users" size={26} /></span>
      <div style={{ minWidth: 0 }}>
        <h1 className="pt-title">Пациенти</h1>
        <div className="pt-subtitle">Регистър на пациентите в практиката</div>
      </div>
    </div>
  </div>
);

/* ---------------------------------------------------------------- */
/* Search input                                                     */
/* ---------------------------------------------------------------- */
const SearchBox = ({ value, onChange, onSubmit }) => (
  <form className="pt-search" onSubmit={onSubmit}>
    <FeatherIcon icon="search" size={16} />
    <input
      type="search"
      placeholder="Търсене по име..."
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Търсене по име"
    />
  </form>
);

/* ---------------------------------------------------------------- */
/* Desktop — table                                                  */
/* ---------------------------------------------------------------- */
const PatientRow = ({ patient, onOpen }) => {
  const age = ageDisplay(patient);
  const egn = fmtEgn(patient.egn);
  return (
    <tr className="pt-row" onClick={() => onOpen(patient)}>
      <td>
        <div className="pt-name-cell">
          <span className="pt-avatar">{initials(patient.fullName)}</span>
          <span className="pt-name-wrap">
            <Link className="pt-name" to={patientHref(patient)} onClick={(e) => e.stopPropagation()}>
              {patient.fullName}
            </Link>
            {egn && <span className="pt-egn">ЕГН&nbsp;·&nbsp;{egn}</span>}
          </span>
        </div>
      </td>
      <td><StatusPill tone="neutral" size="sm">{GENDER_LABEL[patient.gender] || '—'}</StatusPill></td>
      <td><span className="pt-dob">{dobDisplay(patient)}</span></td>
      <td><span className="pt-age">{age != null ? age : '—'} {age != null && <small>г.</small>}</span></td>
    </tr>
  );
};

const PatientTable = ({ patients, onOpen }) => (
  <table className="pt-table">
    <thead>
      <tr>
        <th>Име</th>
        <th>Пол</th>
        <th>Дата на раждане</th>
        <th className="col-num">Възраст</th>
      </tr>
    </thead>
    <tbody>
      {patients.map((p) => <PatientRow key={p.id} patient={p} onOpen={onOpen} />)}
    </tbody>
  </table>
);

/* ---------------------------------------------------------------- */
/* Mobile — patient card                                            */
/* ---------------------------------------------------------------- */
const PatientCard = ({ patient }) => {
  const age = ageDisplay(patient);
  const egn = fmtEgn(patient.egn);
  return (
    <Link className="pt-card-link" to={patientHref(patient)}>
      <Card flat padding="compact">
        <div className="pt-card">
          <span className="pt-avatar">{initials(patient.fullName)}</span>
          <div className="pt-card-body">
            <span className="pt-card-name">{patient.fullName}</span>
            {egn && <span className="pt-egn">ЕГН&nbsp;·&nbsp;{egn}</span>}
            <div className="pt-card-meta">
              <span className="pt-meta-item">
                <FeatherIcon icon="user" size={13} /> {GENDER_LABEL[patient.gender] || '—'}
              </span>
              <span className="pt-meta-item">
                <FeatherIcon icon="calendar" size={13} /> {dobDisplay(patient)}
              </span>
              {age != null && <span className="pt-meta-item mono">{age} г.</span>}
            </div>
          </div>
          <span className="pt-chevron"><FeatherIcon icon="chevron-right" size={20} /></span>
        </div>
      </Card>
    </Link>
  );
};

/* ---------------------------------------------------------------- */
/* Pagination footer                                                */
/* ---------------------------------------------------------------- */
const Pager = ({ page, pageSize, total, onPage }) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);
  return (
    <div className="pt-pager">
      <span className="pt-pager-info">
        <span className="mono">{from}–{to}</span> от <span className="mono">{total}</span>
      </span>
      <div className="pt-pager-btns">
        <button type="button" className="pager-btn" disabled={page === 0} onClick={() => onPage(page - 1)}>
          <FeatherIcon icon="chevron-left" size={14} /> Назад
        </button>
        <button type="button" className="pager-btn" disabled={page >= pages - 1} onClick={() => onPage(page + 1)}>
          Напред <FeatherIcon icon="chevron-right" size={14} />
        </button>
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------- */
/* Page                                                             */
/* ---------------------------------------------------------------- */
const PatientsListBulgarian = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchActive, setSearchActive] = useState(false);
  const [serverPatients, setServerPatients] = useState([]); // current server page
  const [searchResults, setSearchResults] = useState([]);   // full result set (search mode)
  const [page, setPage] = useState(0);                        // 0-based
  const [total, setTotal] = useState(0);

  // Server-paginated listing (default, non-search mode).
  useEffect(() => {
    if (searchActive) return;
    loadPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchActive]);

  const loadPatients = async () => {
    try {
      setLoading(true);
      const res = await patientApi.getPatientsPaginated(page, PAGE_SIZE, 'createdAt', 'desc');
      setServerPatients(res.content);
      setTotal(res.totalElements);
    } catch (error) {
      console.error('Failed to load patients:', error);
      message.error('Не успяхме да заредим списъка с пациенти');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    const q = searchTerm.trim();
    if (!q) {
      // Back to normal server-paginated listing.
      setSearchActive(false);
      setSearchResults([]);
      setPage(0);
      return;
    }
    try {
      setLoading(true);
      const res = await patientApi.searchPatients(q);
      setSearchResults(res);
      setTotal(res.length);
      setSearchActive(true);
      setPage(0);
    } catch (error) {
      console.error('Error searching patients:', error);
      message.error('Грешка при търсене на пациенти');
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchActive(false);
    setSearchResults([]);
    setPage(0);
  };

  // Rows visible on the current page (search mode paginates client-side).
  const visible = useMemo(() => {
    if (!searchActive) return serverPatients;
    return searchResults.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  }, [searchActive, serverPatients, searchResults, page]);

  const openPatient = (p) => navigate(patientHref(p));
  const empty = !loading && visible.length === 0;

  const breadcrumb = (
    <ul className="breadcrumb">
      <li className="breadcrumb-item"><Link to="/patients">Пациенти</Link></li>
      <li className="breadcrumb-item"><FeatherIcon icon="chevron-right" /></li>
      <li className="breadcrumb-item active">Списък пациенти</li>
    </ul>
  );

  return (
    <PageShell sidebarActive="patients-list" header={breadcrumb} width="wide">
      <PageHero />

      <div className="pt-stats">
        <Kpi label="Общо пациенти" value={<CountUp value={total} />} icon={<FeatherIcon icon="users" size={16} />} />
      </div>

      <Section
        title="Списък с пациенти"
        icon={<FeatherIcon icon="user" size={18} />}
        actions={<SearchBox value={searchTerm} onChange={setSearchTerm} onSubmit={handleSearch} />}
      >
        {loading ? (
          <div className="text-center py-4"><Spin /></div>
        ) : empty ? (
          <EmptyState
            icon={<FeatherIcon icon="search" size={26} />}
            title="Няма намерени пациенти"
            description={searchActive
              ? `Няма съвпадения за „${searchTerm}“. Проверете изписването или потърсете по ЕГН.`
              : 'В практиката все още няма регистрирани пациенти.'}
            action={searchActive ? <button type="button" className="pager-btn" onClick={clearSearch}>Изчисти търсенето</button> : null}
          />
        ) : (
          <>
            <div className="pt-desktop-only">
              <PatientTable patients={visible} onOpen={openPatient} />
            </div>
            <div className="pt-mobile-only">
              <div className="pt-cards">
                {visible.map((p) => <PatientCard key={p.id} patient={p} />)}
              </div>
            </div>
            <Pager page={page} pageSize={PAGE_SIZE} total={total} onPage={setPage} />
          </>
        )}
      </Section>

      <style>{`
        .pt-hero {
          position: relative;
          overflow: hidden;
          background: var(--vc-color-primary);
          color: var(--vc-color-text-on-primary);
          border-radius: var(--vc-radius-xl);
          box-shadow: var(--vc-shadow-md);
          padding: var(--vc-space-6);
        }
        .pt-hero-decor {
          position: absolute; inset: 0 0 0 auto;
          width: 320px; pointer-events: none;
          color: #fff; opacity: .10;
          -webkit-mask-image: linear-gradient(90deg, transparent, #000 55%);
                  mask-image: linear-gradient(90deg, transparent, #000 55%);
        }
        .pt-hero-inner { position: relative; display: flex; align-items: center; gap: var(--vc-space-4); }
        .pt-hero-icon {
          flex: none;
          width: 56px; height: 56px;
          border-radius: var(--vc-radius-lg);
          background: rgba(255,255,255,.16);
          box-shadow: inset 0 0 0 1px rgba(255,255,255,.28);
          color: #fff;
          display: grid; place-items: center;
        }
        .pt-title {
          margin: 0;
          font-size: var(--vc-text-2xl);
          font-weight: var(--vc-weight-semibold);
          line-height: var(--vc-leading-tight);
          letter-spacing: .01em;
          color: #fff;
        }
        .pt-subtitle { font-size: var(--vc-text-md); color: rgba(255,255,255,.82); margin-top: 3px; }
        .mono { font-family: var(--vc-font-mono); }

        .pt-stats { display: grid; grid-template-columns: 1fr; gap: var(--vc-space-3); }

        /* Search */
        .pt-search { position: relative; display: flex; align-items: center; width: 100%; }
        .pt-search svg { position: absolute; left: var(--vc-space-3); color: var(--vc-color-text-subtle); pointer-events: none; }
        .pt-search input {
          width: 100%;
          font-family: inherit; font-size: var(--vc-text-base);
          color: var(--vc-color-text);
          padding: var(--vc-space-2) var(--vc-space-3) var(--vc-space-2) calc(var(--vc-space-3) * 2 + 16px);
          border: 1px solid var(--vc-color-border);
          border-radius: var(--vc-radius-md);
          background: var(--vc-color-bg);
          outline: none;
        }
        .pt-search input::placeholder { color: var(--vc-color-text-subtle); }
        .pt-search input:focus { border-color: var(--vc-color-primary); box-shadow: 0 0 0 3px var(--vc-color-primary-soft); }

        /* Desktop table */
        .pt-table { width: 100%; border-collapse: collapse; }
        .pt-table thead th {
          text-align: left;
          font-size: var(--vc-text-xs);
          font-weight: var(--vc-weight-semibold);
          text-transform: uppercase;
          letter-spacing: .05em;
          color: var(--vc-color-text-subtle);
          padding: 0 var(--vc-space-3) var(--vc-space-2);
          border-bottom: 1px solid var(--vc-color-border);
        }
        .pt-row { cursor: pointer; transition: background .12s ease; }
        .pt-row:hover { background: var(--vc-color-primary-soft); }
        .pt-row > td { padding: var(--vc-space-3); border-bottom: 1px solid var(--vc-color-border-soft); vertical-align: middle; }
        .pt-row:last-child > td { border-bottom: none; }
        .pt-name-cell { display: flex; align-items: center; gap: var(--vc-space-3); min-width: 0; }
        .pt-avatar {
          flex: none;
          width: 36px; height: 36px;
          border-radius: var(--vc-radius-pill);
          background: var(--vc-color-primary-soft);
          color: var(--vc-color-primary);
          display: grid; place-items: center;
          font-weight: var(--vc-weight-bold);
          font-size: var(--vc-text-sm);
          letter-spacing: .03em;
        }
        .pt-name-wrap { min-width: 0; display: flex; flex-direction: column; }
        .pt-name {
          font-size: var(--vc-text-md);
          font-weight: var(--vc-weight-semibold);
          color: var(--vc-color-primary);
          text-decoration: none;
          line-height: var(--vc-leading-tight);
        }
        .pt-row:hover .pt-name { text-decoration: underline; }
        .pt-egn {
          font-family: var(--vc-font-mono);
          font-size: var(--vc-text-xs);
          color: var(--vc-color-text-subtle);
          letter-spacing: .04em;
          margin-top: 2px;
        }
        .pt-dob { font-size: var(--vc-text-md); color: var(--vc-color-text); }
        .pt-age { font-family: var(--vc-font-mono); font-size: var(--vc-text-md); color: var(--vc-color-text); }
        .pt-age small { color: var(--vc-color-text-subtle); font-size: var(--vc-text-xs); }

        /* Mobile cards */
        .pt-cards { display: flex; flex-direction: column; gap: var(--vc-space-3); }
        .pt-card-link { text-decoration: none; color: inherit; display: block; }
        .pt-card { display: flex; align-items: center; gap: var(--vc-space-3); }
        .pt-card .pt-avatar { width: 44px; height: 44px; font-size: var(--vc-text-md); }
        .pt-card-body { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: var(--vc-space-2); }
        .pt-card-name {
          font-size: var(--vc-text-md);
          font-weight: var(--vc-weight-semibold);
          color: var(--vc-color-primary);
          line-height: var(--vc-leading-tight);
        }
        .pt-card-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 6px var(--vc-space-3); }
        .pt-meta-item { display: inline-flex; align-items: center; gap: 5px; font-size: var(--vc-text-sm); color: var(--vc-color-text-muted); }
        .pt-meta-item svg { color: var(--vc-color-text-subtle); vertical-align: middle; }
        .pt-chevron { flex: none; color: var(--vc-color-text-subtle); display: inline-flex; }

        /* Pagination */
        .pt-pager {
          display: flex; align-items: center; justify-content: space-between;
          gap: var(--vc-space-3);
          padding-top: var(--vc-space-3);
          margin-top: var(--vc-space-1);
          border-top: 1px solid var(--vc-color-border-soft);
        }
        .pt-pager-info { font-size: var(--vc-text-sm); color: var(--vc-color-text-muted); }
        .pt-pager-info .mono { color: var(--vc-color-text); }
        .pt-pager-btns { display: flex; gap: var(--vc-space-2); }
        .pager-btn {
          appearance: none; cursor: pointer;
          display: inline-flex; align-items: center; gap: 5px;
          font-family: inherit; font-size: var(--vc-text-sm); font-weight: var(--vc-weight-medium);
          color: var(--vc-color-text);
          background: var(--vc-color-bg);
          border: 1px solid var(--vc-color-border);
          border-radius: var(--vc-radius-md);
          padding: var(--vc-space-2) var(--vc-space-3);
        }
        .pager-btn:hover:not(:disabled) { border-color: var(--vc-color-primary); color: var(--vc-color-primary); }
        .pager-btn:disabled { opacity: .45; cursor: not-allowed; }

        /* Responsive: table on desktop, cards on mobile */
        .pt-mobile-only { display: none; }
        @media (max-width: 768px) {
          .pt-desktop-only { display: none; }
          .pt-mobile-only { display: block; }
        }
        @media (min-width: 600px) {
          .vc-section__actions .pt-search { width: 280px; }
        }
        @media (max-width: 480px) {
          .pt-hero { padding: var(--vc-space-5); }
          .pt-title { font-size: var(--vc-text-xl); }
          .pt-hero-decor { width: 220px; }
        }
      `}</style>
    </PageShell>
  );
};

export default PatientsListBulgarian;
